import { mergeRefs } from "@regrello/core-utils";
import React, { useCallback } from "react";
import { type FieldPath, type FieldValues, useController } from "react-hook-form";
import { RegrelloFormFieldNumber, type RegrelloFormFieldNumberProps } from "../RegrelloFormFieldNumber";
import type { FormFieldPropsToOmit } from "./types";
import { getErrorProp, type RegrelloControlledFormFieldProps, useInvokeOnChangeOnRequiredStateChange } from "./utils";

export function RegrelloControlledFormFieldNumberInternal<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  controllerProps,
  onValueChange,
  ...numberFieldProps
}: RegrelloControlledFormFieldProps<
  TFieldValues,
  TName,
  // TODONUM (clewis): With our Number & Currency Field Improvements work, we should be able to
  // delete the onValueChange prop entirely and rely instead on controllerProps automatically
  // listening for onChange in the form.
  Omit<RegrelloFormFieldNumberProps, FormFieldPropsToOmit | "onValueChange">
> & {
  /**
   * Capture the change event before the rerender happens. This is useful for setting the "useForm"
   * form values to the correct type before rendering a new RegrelloFormField of a different type.
   * This avoids passing in a incorrect data type to the component.
   *
   * @deprecated You shouldn't need to use this; the provided controllerProps should listen for
   * `onChange` automatically.
   */
  onValueChange?: (name: string, newValue?: number | null) => void;
}) {
  const { field, fieldState } = useController(controllerProps);
  const { ref, value, onBlur: internalOnBlur, onChange: internalOnChange, ...fieldProps } = field;

  const propsOnBlur = numberFieldProps.onBlur;

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      internalOnBlur();
      propsOnBlur?.(event);
    },
    [internalOnBlur, propsOnBlur],
  );

  useInvokeOnChangeOnRequiredStateChange(field, numberFieldProps.isRequiredAsteriskShown ?? false);

  // HACKHACK (clewis): Numbers sometimes come in as strings. Handle this gracefully. Would be nice
  // to do a larger refactor though.
  const valueAsNumber =
    typeof value === "string" && Number.isFinite(Number.parseFloat(value))
      ? Number.parseFloat(value)
      : Number.isFinite(value)
        ? value
        : null;

  return (
    <RegrelloFormFieldNumber
      {...fieldProps}
      {...numberFieldProps}
      {...getErrorProp(fieldState)}
      inputRef={numberFieldProps.inputRef != null ? mergeRefs(ref, numberFieldProps.inputRef) : ref}
      onBlur={handleBlur}
      onValueChange={(nextValue) => {
        internalOnChange(nextValue);
      }}
      value={valueAsNumber}
    />
  );
}

export const RegrelloControlledFormFieldNumber = React.memo(
  RegrelloControlledFormFieldNumberInternal,
) as typeof RegrelloControlledFormFieldNumberInternal;
